namespace OnlineDPR.Application.Shared

open OnlineDPR.Application.Shared.Translations

#if FABLE_COMPILER
open Thoth.Json
#else
open Thoth.Json.Net
#endif

type Redirect = { Url: string }

type AddedUser = { Login: string
                   Password: string
                   EMail: string option }

type UserSyncResult = { AddedUsers: AddedUser list
                        ClubsWithoutCounty: string list
                        ClubsWithMultipleCountries: string list
                        LoginsWithoutEMail: string list }

type UserSyncModel = { File: byte[]
                       Result: UserSyncResult option
                       Pending: bool option
                       Success: bool
                       ErrorMessage: string option }

type FieldType =
| Int
| Decimal
| String
| Date of DateOptions
| SelectOne of SelectOneOptions
and DateOptions = { Min: DayDate option; Max: DayDate option}
and SelectOneOptions = { Items: SelectListItem list }
and SelectListItem = { DisplayName: string
                       Value: string }

type ValidationError = { MessageKey: TranslationKey
                         Params: obj array }
                       static member Init key ps =
                                        { MessageKey = key
                                          Params = ps }
                       static member KeyOnly key =
                                          { MessageKey = key
                                            Params = [||] }


type Field = { Key: string
               LabelTranslationKey: TranslationKey option
               Label: string option
               Value: string
               FieldType: FieldType
               Postfix: string option
               HideLabel: bool
               IsMandatory: bool
               IsReadOnly: bool
               Invalid: bool
               ValidationMessage: ValidationError option }
             static member Init key fieldType (f: Field -> Field) =
                            f { Key = key
                                LabelTranslationKey = None
                                Value = ""
                                FieldType = fieldType
                                Postfix = None
                                HideLabel = false
                                IsMandatory = false
                                IsReadOnly = false
                                Invalid = false
                                ValidationMessage = None
                                Label = None }
             static member WithValidationMessage field error =
                            { field with ValidationMessage = error }

type DprInputFormRow =
    | FieldWithLevel of Level: int* Field: Field
    | CalculatedField of Level: int* Text: TranslationKey
    | Heading of Level : int*Text: TranslationKey

type DprInputModel = { Date: DayDate
                       Fields : DprInputFormRow list
                       GeneralErrors: ValidationError list
                       SubmitErrors: bool
                       DisplayModal: bool
                       ClubId: int
                       ClubNumber: string
                       Template: Template }

type DprInputServerValidationResult =
                     { FieldsWithErrors : Field list
                       GeneralErrors : ValidationError list }

type SubmitedDprResult =
    | Redirect of Redirect
    | DprInputServerValidationResult of DprInputServerValidationResult

type BwaFields = { Date : MonthDate
                   DateString: string
                   ClubId: int
                   ValuesAreEstimated: bool

                   // BWA-Value
                   NettoSalesFromMembers: decimal
                   NettoIncomeFromPelviPower: decimal
                   NettoIncomeFromBoots: decimal
                   NettoIncomeFromFusionetics : decimal
                   NettoIncomeFromKilosWeg : decimal
                   NettoOtherSales: decimal
                   NettoSalesTotal: decimal
                   SalaryAndSocial: decimal
                   NettoRentAndAdditionalCosts: decimal
                   NettoLocalMarketingCosts: decimal
                   NettoOtherCosts:  decimal
                   NettoCostsTotal: decimal
                   ResultEBITDA: decimal

                   // other
                   CurrencySymbol: string }

type BwaInputModel = { Date: MonthDate
                       DateString: string
                       ClubId: int
                       HistoryFields : BwaFields list

                       // BWA-Values
                       NettoSalesFromMembers: Field
                       NettoIncomeFromPelviPower: Field
                       NettoIncomeFromBoots: Field
                       NettoIncomeFromFusionetics : Field
                       NettoIncomeFromKilosWeg : Field

                       NettoOtherSales: Field
                       NettoSalesTotal: decimal //calculated

                       SalaryAndSocial: Field
                       NettoRentAndAdditionalCosts: Field
                       NettoLocalMarketingCosts: Field
                       NettoOtherCosts: Field
                       NettoCostsTotal: decimal //calculated
                       ResultEBITDA: decimal //calculated

                       // other
                       Currency: string

                       PostUrl: string

                       PendingRequest: bool

                       Template : Template }

type NotificationState =
| Todo
| Done

type Notification =
    { State: NotificationState
      Title: string
      Text: string
      Links: NotificationLink list }

and NotificationLink =
| Weblink of Link
| Hide of Link

and Link =
    { Text: string
      Uri: string }

type Invoice =
    { InvoiceId: int
      InvoiceNumber: string
      InvoiceDate: string
      InvoiceDateTimeStamp: int
      DueDate: string
      DueDateTimeStamp: int
      ClubNumber: string
      Product: string
      Amount: string
      AmountValue: decimal
      URl: string
      UserNotes: string option}

type InvoiceSortColumn =
    | NoColumn
    | Number
    | Date
    | DueDate
    | ClubNumber
    | Product
    | Amount
    | Note

type InvoiceSortOrder =
    | Ascending
    | Descending

type InvoiceSortOption = {
    Column: InvoiceSortColumn
    Order: InvoiceSortOrder
}

type ControlPanelModel =
    { Notifications: Notification list
      Template: Template }

type ControlPanelBwaModel =
    { Invoices: Invoice list
      SortOption: InvoiceSortOption
      Template: Template }

type DprClubOptions =
    { ClubId: int
      ClubNumber: string
      IsOnFitUser: bool
      SyncDataFromOnfitAllowed: bool
      IsBlacklisted: bool }

type ControlPanelDprModel =
    { Template: Template
      ClubOptions: DprClubOptions list }

and EditRowModel =
    { ClubNumber: Field
      Date: Field
      ErrorMessage: string option
      PendingRequest: bool }

type EditDprRequest =
    { ClubId: int
      SelectedDay: DayDate }

type EditRequestResult =
    | EditRequestSuccess of Redirect
    | EditRequestError of string
    | EditRequestSuccessMessage of string

type UpdateTranslationsModel =
    { Pending: bool option }

type BwaOverviewModel =
    { EditBwa: EditRowModel }

type EditBwaRequest =
    { ClubNumber: string
      SelectedMonth: MonthDate }

type CreateDprNotificationRequest =
    { ClubNumber: string
      SelectedDay: DayDate }

type DprAdministrationModel =
    { AddDprNotification: EditRowModel
      ValidClubs: string list }

type ClubUser =
    { Email: string option
      UserId: int
      Name: string
      HasSentPasswordRecovery: bool }

type ClubData =
    { ClubNumber: string
      ClubId: int
      B2B: string
      FranchisePartnerId: int
      FranchisePartnerName: string
      User: ClubUser list
      SyncDataFromOnfitAllowed: bool
      IsOnfitUser: bool
      IsUpdating: bool }

type InvoicesModel = {
    Invoices: Invoice list
    Searchterm: string
    SortOption: InvoiceSortOption
}

type ReportMenuEntryLevelType =
    | God = 60
    | Country = 50
    | Region = 40
    | MultiRegion = 30
    | FranchisePartner = 20
    | Club = 10
    | Employee = 0

type ReportDisplayInterval =
    | OneMonth = 1
    | Quarter = 3
    | HalfYear = 6
    | Year = 12
    | SingleDays = 30
    | TwoYears = 24
    | TotalPeriod = 99

type ReportChartType =
    | Bar = 1
    | Line = 2

type DprOrigin =
    | Unknown = 0
    | OldDataImport = 1
    | Sync = 2
    | Manual = 3

type ReportCategory =    { FieldName: string
                           TranslationKey: TranslationKey option
                           Name: string
                           IsHeadline: bool             // z.B. für fette Darstellung
                           IncludeInReport: bool
                           DataRow: decimal list
                           AggregatedSumValue: decimal
                           AggregatedAverageValue: decimal
                           Suffix: string
                           Subcategories: ReportCategory list
                           SubcategoriesVisible: bool }

                         static member WithSubcategories reportCategory subCategories =
                            { reportCategory with Subcategories = subCategories }

type ReportColumnInfo = {
    Label: string
    Origin: DprOrigin
}

type ReportViewModel = { Columns: ReportColumnInfo list
                         Categories: ReportCategory list }

type ReportFilterViewModel = { Level: ReportMenuEntryLevelType
                               LevelIdentifier: int
                               From: DayDate
                               Interval: ReportDisplayInterval }

type ReportMenuEntry = { Name: string
                         Level: ReportMenuEntryLevelType
                         LevelIdentifier: int
                         SubEntries: ReportMenuEntry list }

type ReportChartOptions = { ReportChartType: ReportChartType
                            DisplayDatalabels: bool }

type ReportModel = { Data: ReportViewModel
                     Count: int
                     Filter: ReportFilterViewModel
                     Menu: ReportMenuEntry
                     Template: Template
                     ChartOptions: ReportChartOptions
                     Updating: bool
                     EditDpr: EditRowModel
                     ShowEditDpr: bool
                     ShowChart: bool}

type PageModel =
| BlankPageModel
| ReportPageModel of ReportModel
| UserSyncPageModel of UserSyncModel
| DprInputModel of DprInputModel
| BwaInputModel of BwaInputModel
| ControlPanelModel of ControlPanelModel
| ControlPanelBwaModel of ControlPanelBwaModel
| ControlPanelDprModel of ControlPanelDprModel
| UpdateTranslationsModel of UpdateTranslationsModel
| BwaOverviewModel of BwaOverviewModel
| ClubListModel of ClubData list
| InvoicesModel of InvoicesModel
| DprAdministrationModel of DprAdministrationModel

type Model = { PageModel: PageModel }

module DprInput =

    let initDprInputModel template date fields clubId clubNumber =
        { PageModel = DprInputModel { Date = date
                                      Fields = fields
                                      ClubId = clubId
                                      GeneralErrors = []
                                      SubmitErrors = false
                                      DisplayModal = false
                                      ClubNumber = clubNumber
                                      Template = template }}

module BwaInput =
    let initBwaInputModel template date dateString clubId historyFields currency posturl =
        let initField key translationKey fieldType value isMandatory =
            Field.Init key fieldType (fun f -> { f with Value = match value with
                                                                | Some v -> (sprintf "%M" v).Replace('.',',')
                                                                | None -> ""
                                                        IsMandatory = isMandatory
                                                        Postfix = Some currency })

        { PageModel = BwaInputModel {
            Date = date
            DateString = dateString
            ClubId = clubId
            HistoryFields = historyFields
                            |> List.skip 1

            NettoSalesFromMembers = initField "NettoSalesFromMembers" TranslationKey.BwaInputMask_NettoSalesFromMembers FieldType.Decimal (Some historyFields.[0].NettoSalesFromMembers) true
            NettoIncomeFromPelviPower = initField "NettoIncomeFromPelviPower" TranslationKey.BwaInputMask_NettoIncomeFromPelviPower FieldType.Decimal (Some historyFields.[0].NettoIncomeFromPelviPower) true
            NettoIncomeFromBoots = initField "NettoIncomeFromBoots" TranslationKey.BwaInputMask_NettoIncomeFromBoots FieldType.Decimal (Some historyFields.[0].NettoIncomeFromBoots) true
            NettoIncomeFromFusionetics = initField "NettoIncomeFromFusionetics" TranslationKey.BwaInputMask_NettoIncomeFromFusionetics FieldType.Decimal (Some historyFields.[0].NettoIncomeFromFusionetics) true
            NettoIncomeFromKilosWeg = initField "NettoIncomeFromKilosWeg" TranslationKey.BwaInputMask_NettoIncomeFromKilosWeg FieldType.Decimal (Some historyFields.[0].NettoIncomeFromKilosWeg) true
            NettoOtherSales = initField "NettoOtherSales" TranslationKey.BwaInputMask_NettoOtherSales FieldType.Decimal (Some historyFields.[0].NettoOtherSales) true
            NettoSalesTotal = historyFields.[0].NettoSalesTotal

            SalaryAndSocial = initField "SalaryAndSocial" TranslationKey.BwaInputMask_SalaryAndSocial FieldType.Decimal (Some historyFields.[0].SalaryAndSocial) true
            NettoRentAndAdditionalCosts = initField "NettoRentAndAdditionalCosts" TranslationKey.BwaInputMask_NettoRentAndAdditionalCosts FieldType.Decimal (Some historyFields.[0].NettoRentAndAdditionalCosts) true
            NettoLocalMarketingCosts = initField "NettoLocalMarketingCosts" TranslationKey.BwaInputMask_NettoLocalMarketingCosts FieldType.Decimal (Some historyFields.[0].NettoLocalMarketingCosts) true
            NettoOtherCosts = initField "NettoOtherCosts" TranslationKey.BwaInputMask_NettoOtherCosts FieldType.Decimal (Some historyFields.[0].NettoOtherCosts) true
            NettoCostsTotal = historyFields.[0].NettoCostsTotal
            ResultEBITDA = historyFields.[0].ResultEBITDA

            PendingRequest = false

            PostUrl = posturl

            Currency = currency
            Template = template }}

    let NettoSalesTotalCalculated (f:BwaFields) =
        f.NettoSalesFromMembers +
        f.NettoIncomeFromPelviPower +
        f.NettoIncomeFromBoots +
        f.NettoIncomeFromFusionetics +
        f.NettoIncomeFromKilosWeg +
        f.NettoOtherSales

    let NettoCostsTotalCalculated (f:BwaFields) =
        f.SalaryAndSocial + f.NettoRentAndAdditionalCosts + f.NettoLocalMarketingCosts + f.NettoOtherCosts

    let ResultEBITDACalculated (f:BwaFields) =
        (NettoSalesTotalCalculated f) - (NettoCostsTotalCalculated f)


module ControlPanel =
    let initControlPanelModel notifications =
        { PageModel = ControlPanelModel { Notifications = notifications
                                          Template = Template.Default } }

module ControlPanelBwa =
    let init documents =
        { PageModel = ControlPanelBwaModel { Invoices = documents
                                             SortOption = { Column = InvoiceSortColumn.Date
                                                            Order = InvoiceSortOrder.Descending }
                                             Template = Template.Default } }

module EditDpr =
    let create (clubs: SelectListItem list) minDate maxDate =
        let initField key fieldType value hideLabel labelTranslationKey =
            Field.Init key fieldType (fun f -> { f with IsMandatory = false
                                                        Value = value
                                                        HideLabel = hideLabel
                                                        LabelTranslationKey = labelTranslationKey })
        let clubValue = match clubs |> List.tryHead with
                        | Some i -> i.Value
                        | None -> ""
        { ClubNumber = initField "club" (FieldType.SelectOne { Items = clubs } ) clubValue false (Some TranslationKey.ControlPanel_Dpr_EditDpr_ClubLabelText)
          Date = initField "date" (FieldType.Date {Min = minDate; Max = maxDate}) "" true None
          ErrorMessage = None
          PendingRequest = false }

module ControlPanelDpr =
    let init (syncoptions:DprClubOptions list) =
        let items = syncoptions
                    |> List.map (fun o -> { DisplayName = o.ClubNumber; Value = string o.ClubId })
        { PageModel = ControlPanelDprModel { Template = Template.Default
                                             ClubOptions = syncoptions} }


module Report =
    let init filter menu clubs minDate maxDate =
        { PageModel = ReportPageModel { Data = { Columns = []
                                                 Categories = [] }
                                        Count = 0
                                        Filter = filter
                                        Menu = menu
                                        Template = Template.Default
                                        ChartOptions = { ReportChartType = ReportChartType.Bar
                                                         DisplayDatalabels = true }
                                        Updating = true
                                        EditDpr = EditDpr.create clubs minDate maxDate
                                        ShowEditDpr = false
                                        ShowChart = true } }